"use client"

import { LightningIcon, Pill, Text } from "@/components/ui"

interface PdpOptionPickerProps {
  title: string
  values: string[]
  selected?: string
  onSelect: (value: string) => void
  isValueAvailable?: (value: string) => boolean
  note?: string
  prompt?: string
  promptId?: string
  className?: string
}

const formatValue = (value: string) => value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()

export const PdpOptionPicker = ({ title, values, selected, onSelect, isValueAvailable, note, prompt, promptId, className }: PdpOptionPickerProps) => (
  <div className={`flex flex-col gap-125 ${prompt ? "ring-brand rounded-8x -m-250 p-250 ring-2" : ""} ${className ?? ""}`}>
    <Text as="div" variant="bodyLg" family="text" tone="inherit" className="text-little @3xl:text-body-lg text-fg-heading font-medium">
      {title}
      {selected != null && <span className="hidden @3xl:inline">{`: ${formatValue(selected)}`}</span>}
    </Text>
    <div className="flex flex-wrap gap-125" role="group" aria-label={title}>
      {values.map((value) => {
        const available = isValueAvailable?.(value) ?? true
        const isSelected = value === selected
        return (
          <Pill
            key={value}
            tone="outline"
            size="sm"
            selected={isSelected}
            disabled={!available}
            onClick={() => onSelect(value)}
            className={`text-little @3xl:text-little-lg font-text h-9 px-300 font-normal tabular-nums @3xl:h-10.25 @3xl:px-375 @3xl:tracking-normal ${isSelected ? "border-line-brand bg-surface text-fg-brand hover:border-line-brand hover:text-fg-brand hover:opacity-100" : "border-line text-fg-heading"} ${available ? "" : "text-fg-faint line-through"}`}
          >
            {formatValue(value)}
          </Pill>
        )
      })}
    </div>
    <p id={promptId} aria-live="polite" className={prompt ? "font-text text-little text-fg-brand font-medium" : "sr-only"}>
      {prompt}
    </p>
    {note && (
      <Text as="div" variant="caption" family="display" tone="sale" className="flex items-center gap-100 font-semibold">
        <LightningIcon size={12} />
        {note}
      </Text>
    )}
  </div>
)
